import { DataAction, Payload, Persistence, StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Injectable } from '@angular/core';
import { Selector, State, Store } from '@ngxs/store';
import { produce } from 'immer';
import { combineLatest, filter, first, skip, tap } from 'rxjs';
import { Role } from 'src/app/models/role';
import { PermissionType } from 'src/app/models/user';
import { IPreviewFeature } from 'src/app/services/app-initializer/app-initializer.service';
import { ConfigsService } from 'src/app/services/configs/configs.service';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';
import { MemberService } from 'src/app/services/member/member.service';
import { UserPermissionService } from 'src/app/services/user/user-permission.service';
import { UserService } from 'src/app/services/user/user.service';
import { Utilities } from 'src/app/services/utilities';
import { environment } from 'src/environments/environment';
import { IPreviewFeatureStateModel } from './preview-feature.models';

@Persistence()
@StateRepository()
@State<IPreviewFeatureStateModel>({
    name: 'previewFeatureState',
    defaults: {
        selectedPreviews: []
    }
})
@Injectable()
export class FeatureService extends NgxsDataRepository<IPreviewFeatureStateModel> {
    tenantId: string;

    @Selector()
    static initialized(state: IPreviewFeatureStateModel) {
        return state.initialized;
    }

    get initialized$() {
        return this.store.select<boolean>(FeatureService.initialized);
    }

    featureKey: string = "FEATURE";
    featureNamespace: string = "UI.Feature";

    displaySelection: boolean;
    permissions: any;
    defaultCacheDuration: number = 1800000; // 30 minutes

    get activated() {
        return !!environment.activePreviewFeature;
    }

    constructor(private readonly store: Store,
        private readonly configService: ConfigsService,
        private readonly userPermissionService: UserPermissionService) {
        super();
    }

    async ngxsAfterBootstrap() {
        super.ngxsAfterBootstrap();

        // Wait until authenticated to get access to access_token. UserService.userIdentifier need to be replaced by something more specific to access token.

        const currentRole$ = this.store.select<Role>(UserService.currentRole);
        const tenantId$ = this.store.select<string>(MemberService.tenantId).pipe(filter(tenantId => tenantId != this.tenantId), tap(tenantId => { this.tenantId = tenantId }));

        await combineLatest([this.store.select<string>(UserService.userIdentifier), currentRole$,
        this.store.select<boolean>(ConfigurationService.initializedSelector)]).pipe(filter(([userIdentifier, configInitialized, currentRole$]) => !!userIdentifier && !!configInitialized && !!currentRole$), first()).toPromise();

        // Purge out obsolete features from cache.
        this.deleteObsoleteFeatures();

        await this.userPermissionService.registerPermissions([{ selectionName: this.featureNamespace, permissionType: PermissionType.namespace }]);

        currentRole$.pipe(skip(1)).subscribe(async selectedRole => {
            await this.featureRoleChange();
        })

        tenantId$.subscribe(async tenantId => {
            await this.featureTenantChange(tenantId);
        })
    }

    toggleForm() {
        this.displaySelection = !this.displaySelection;
    }

    private async featureRoleChange() {

        await this.userPermissionService.registerPermissions([{ selectionName: this.featureNamespace, permissionType: PermissionType.namespace }]);

        try {
            this.setInitialized(false);

            let dynamicFeatures = Utilities.cloneHard(this.snapshot.selectedPreviews);
            dynamicFeatures.forEach(async current => {

                this.initializeFeature(current.id, current.data, true);
            });
        } finally {
            this.setInitialized(true);
        }
    }

    private async featureTenantChange(tenantId: string) {

        try {
            this.setInitialized(false);

            let dynamicFeatures = Utilities.cloneHard(this.snapshot.selectedPreviews);
            dynamicFeatures.forEach(async current => {

                if (current?.data?.tenantId == undefined) {
                    return;
                }

                if (current?.data?.tenantId != undefined && current?.data?.tenantId != tenantId) {
                    this.setPreviewItem({ ...current, inScope: false });
                }
                else {
                    this.initializeFeature(current.id, current.data);
                }
            });
        } finally {
            this.setInitialized(true);
        }
    }

    async initializeFeature(feature: string, data: any = {}, forceReload: boolean = false) {

        this.setInitialized(false);

        try {
            const hash = window.btoa(feature + JSON.stringify(data));

            const dynamicFeatures = this.snapshot.selectedPreviews;
            dynamicFeatures.forEach(async current => {
                if (current.id == feature) {
                    this.setPreviewItem({ ...current, inScope: false });
                }
            });

            const cachedRequestedFeature = this.snapshot.selectedPreviews.find(item => item.hash && item.hash === hash);

            if (!forceReload) {
                // check the cache for the item, need to see it the cache timedout
                if (cachedRequestedFeature != undefined) {
                    const isCacheValid = (new Date().getTime() - new Date(cachedRequestedFeature?.timeStamp).getTime()) < (cachedRequestedFeature.cacheDuration ?? this.defaultCacheDuration);
                    if (!isCacheValid) {
                        // scope it to false, it will reset if it resolves again, add the new timestamp
                        this.setPreviewItem({ ...cachedRequestedFeature, inScope: false, timeStamp: new Date() });
                    }
                    else {
                        // scope it to true
                        this.setPreviewItem({ ...cachedRequestedFeature, inScope: true });
                        return;
                    }
                }
            }

            //
            // try and get the requested feature
            //
            const permissions = this.userPermissionService.getPermissionsByNameSpace(this.featureNamespace).map(p => p.permission.name).join(',');

            let dataParam = Utilities.cloneHard(data);
            dataParam.Feature = feature;

            let item = await this.configService.firstOrDefault(this.featureKey, { ...{ Permissions: permissions }, ...dataParam });

            if (item.value == null) {
                // if we had it in the cache and it no longer resolves we can clean up.
                if (cachedRequestedFeature != undefined) {
                    this.deletePreviewItem(cachedRequestedFeature);
                }
                return;
            }

            const requestedFeature = { ...JSON.parse(item?.value), inScope: true, hash: hash, timeStamp: new Date(), data: data, id: feature };

            //
            // We need to load or update the feature and set the cache timeout
            //
            const currentFeatures = this.snapshot.selectedPreviews;
            const index = currentFeatures.findIndex(item => item.hash && item.hash === hash);

            if (index === -1) {
                this.setPreviewItem(requestedFeature);
            } else {
                const isEnabled = requestedFeature.enabled == undefined || requestedFeature.enabled;
                this.setPreviewItem(isEnabled ? { ...requestedFeature, active: currentFeatures[index].active } : requestedFeature);
            }
        } finally {
            this.setInitialized(true);
            console.log("SETPREVIEW", this.snapshot.selectedPreviews);
        }
    }

    hasFeature(previewIdentifier: string) {
        if (this.activated) {
            const preview = this.snapshot.selectedPreviews.find(item => item.id === previewIdentifier);
            return !!preview && preview.active && preview.inScope;
        } else {
            return false;
        }
    }

    activeFeatures() {
        return {
            activeFeatures: (this.snapshot.selectedPreviews || [])
                .filter(feature => !!feature && feature.active && feature.inScope)
                .map(preview => ({ Name: preview.id }))
        };
    }

    @DataAction()
    setPreviewItem(@Payload('previews') newPreview: IPreviewFeature) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: IPreviewFeatureStateModel) => {
            let selectedPreviews = [...draft.selectedPreviews.filter(f => f.hash != newPreview.hash)].sort((a, b) => (b.id < a.id ? 1 : -1));
            draft.selectedPreviews = [...selectedPreviews, newPreview].sort((a, b) => (b.id < a.id ? 1 : -1));
        }));
    }

    @DataAction()
    deletePreviewItem(@Payload('delete') newPreview: IPreviewFeature) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: IPreviewFeatureStateModel) => {
            draft.selectedPreviews = [...draft.selectedPreviews.filter(f => f.hash != newPreview.hash)].sort((a, b) => (b.id < a.id ? 1 : -1));
        }));
    }

    @DataAction()
    deleteObsoleteFeatures() {
        this.ctx.setState(produce(this.ctx.getState(), (draft: IPreviewFeatureStateModel) => {
            draft.selectedPreviews = [...draft.selectedPreviews.filter(f => !(f as any).status)].sort((a, b) => (b.id < a.id ? 1 : -1));
        }));
    }

    @DataAction()
    setInitialized(@Payload('initialized') initialized: boolean) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: IPreviewFeatureStateModel) => {
            draft.initialized = initialized;
        }));
    }
}
